import { useTranslation } from "react-i18next";
import { Card, type CardProps, Text } from "react-native-paper";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import Button, { type ButtonProps } from "../Button";
import { FlatCard } from "../Cards/FlatCard";
import { CompletionRing } from "../CompletionRing";
import { InfoChip } from "../InfoChip";
import {
	getButtonIconAndLabel,
	getRecommendedCardLayout,
	getRecommendedCtaMode,
} from "./helpers";
import type { TasksSectionItem } from "./types";

type Props = TasksSectionItem & {
	style?: CardProps["style"];
	/** When set, skips home CTA mode rules (mobile text / desktop by status). */
	ctaMode?: NonNullable<ButtonProps["mode"]>;
};

const RING_SIZE = 48;

export const RecommendedCard = ({
	status,
	title,
	body,
	chips,
	kind,
	completion = 0,
	icon,
	loading: isLoading = false,
	cardWidth,
	style,
	ctaMode,
	onPress,
}: Props) => {
	const { t } = useTranslation();
	const { isMobile, isSmallScreen } = useDevice();
	const buttonInfo = getButtonIconAndLabel(status, kind);
	const hasChips = Boolean(chips?.length);
	const buttonMode = ctaMode ?? getRecommendedCtaMode(status, isMobile);
	const { stackFooter, useCompactChips } = getRecommendedCardLayout({
		cardWidth,
		isMobile,
		isSmallScreen,
	});

	return (
		<FlatCard
			testID="recommended-card"
			style={[{ width: cardWidth, flex: 1 }, style]}
			contentStyle={{ flexGrow: 1 }}
		>
			<Card.Content
				style={{
					flexGrow: 1,
					padding: 16,
					gap: 16,
					justifyContent: "space-between",
				}}
			>
				<Box flexDirection="row" alignItems="center" gap={8}>
					<CompletionRing progress={completion} icon={icon} size={RING_SIZE} />
					<Box flex={1} gap={4}>
						<Text variant="titleMedium" numberOfLines={2}>
							{title}
						</Text>
						{!isMobile && body ? (
							<Text variant="bodyMedium">{body}</Text>
						) : null}
					</Box>
				</Box>
				<Box
					width="100%"
					flexDirection={stackFooter ? "column" : "row"}
					alignItems={stackFooter ? "flex-start" : "center"}
					justifyContent={hasChips ? "space-between" : "flex-end"}
					gap={stackFooter ? 8 : 16}
				>
					{chips?.length ? (
						<Box
							flexDirection="row"
							gap={4}
							flexShrink={1}
							minWidth={0}
							maxWidth="100%"
						>
							{chips.map((chip) => (
								<InfoChip
									key={chip.label}
									icon={chip.icon}
									label={
										useCompactChips
											? (chip.compactLabel ?? chip.label)
											: chip.label
									}
								/>
							))}
						</Box>
					) : null}
					<Box flexShrink={0}>
						<Button
							disabled={isLoading}
							mode={buttonMode}
							icon={buttonInfo?.icon}
							onPress={onPress}
							loading={isLoading}
						>
							{buttonInfo?.label ?? t("sessionConfig.start")}
						</Button>
					</Box>
				</Box>
			</Card.Content>
		</FlatCard>
	);
};
